import { Plus, X } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle, DiscardGuard,
} from "@/components/ui/dialog";
import { Callout } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input, Textarea } from "@/components/ui/input";
import { Switch } from "@/components/ui/misc";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { sameValues } from "@/lib/form";
import {
  useCampaigns, useCreateTrigger, useTenants, useTriggerInbox, useTriggerRequest, useUpdateTrigger,
} from "@/lib/queries";
import type { Trigger, TriggerCreated } from "@/lib/types";
import {
  EVERY_CAMPAIGN, FIELD_WORDS, MAX_IDEMPOTENCY_PATHS, NEW_TRIGGER, TRIGGER_ACTIONS, actionTerm,
  requestPaths, triggerChanges, triggerPayload, triggerProblem, triggerValues, type MappingField,
  type TriggerValues,
} from "@/lib/triggers";

/** Radix's Select cannot hold an empty value, so every campaign has a value of its own here. */
const ALL = "all";
const PATHS_LIST = "trigger-request-paths";

/**
 * Make a trigger, or edit one (ADR-021): what it does, which campaign it touches, and where in a
 * request it finds what the action needs.
 *
 * A new trigger starts as a zap's new-lead trigger with its fields named as "How to call it" tells a
 * zap to name them, so the common case is a name, a campaign and the consent wording. On an edit,
 * the paths in the trigger's newest request are offered in every path field: the way to map a sender
 * is to send one request to a trigger and read what arrived.
 */
export function TriggerFormDialog({
  open, onOpenChange, trigger, onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Absent for create. */
  trigger?: Trigger;
  /** After a create: the trigger with its secret, which is shown this once. */
  onCreated?: (created: TriggerCreated) => void;
}) {
  const toast = useToast();
  const { user: actor } = useAuth();
  const create = useCreateTrigger();
  const update = useUpdateTrigger();
  const campaigns = useCampaigns({}, { enabled: open });
  const isSuperadmin = actor?.role === "superadmin";
  const { data: tenants } = useTenants({ enabled: open && isSuperadmin && !trigger });
  const pickTenant = isSuperadmin && !trigger && (tenants?.length ?? 0) > 1;
  // The newest request, for the paths it holds.
  const inbox = useTriggerInbox(open ? trigger?.id : undefined);
  const newest = useTriggerRequest(open ? trigger?.id : undefined, inbox.data?.items[0]?.id);
  const suggestions = newest.data?.raw ? requestPaths(newest.data.raw) : [];

  const [values, setValues] = React.useState<TriggerValues>(NEW_TRIGGER);
  // What the form was loaded with, which an edit is measured against.
  const [loaded, setLoaded] = React.useState<TriggerValues>(NEW_TRIGGER);
  const [tenantId, setTenantId] = React.useState<number | null>(null);
  const [touched, setTouched] = React.useState(false);
  const [formError, setFormError] = React.useState<string | null>(null);

  // Loaded when it opens, and for another trigger, but not each time the same trigger is fetched
  // again: a request arriving changes its `last_received_at` while somebody edits, and reloading
  // then wiped what they had typed.
  const latest = React.useRef(trigger);
  latest.current = trigger;
  React.useEffect(() => {
    if (!open) return;
    const start = latest.current ? triggerValues(latest.current) : NEW_TRIGGER;
    setValues(start);
    setLoaded(start);
    setTenantId(null);
    setTouched(false);
    setFormError(null);
  }, [open, trigger?.id]);

  const set = <K extends keyof TriggerValues>(key: K, value: TriggerValues[K]) =>
    setValues((current) => ({ ...current, [key]: value }));
  const setPath = (field: MappingField, path: string) =>
    setValues((current) => ({ ...current, mapping: { ...current.mapping, [field]: path } }));

  const tenant = trigger?.tenant_id ?? tenantId ?? actor?.tenant_id ?? null;
  const tenantCampaigns = (campaigns.data ?? []).filter((c) => tenant === null || c.tenant_id === tenant);
  const problem = triggerProblem(values);
  const changed = !sameValues(values, loaded) || tenantId !== null;
  const action = values.action;
  const enlist = action === "lead.enlist";
  const requireConsent = values.require_consent;
  const capture = action === "capture";

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setTouched(true);
    setFormError(null);
    if (problem) return;
    if (pickTenant && tenantId === null) {
      setFormError("Choose the tenant whose leads it acts on.");
      return;
    }
    try {
      if (trigger) {
        const changes = triggerChanges(values, trigger);
        if (Object.keys(changes).length > 0) await update.mutateAsync({ id: trigger.id, ...changes });
        toast.success("Trigger saved", values.name.trim());
        onOpenChange(false);
      } else {
        const created = await create.mutateAsync({
          ...triggerPayload(values),
          ...(pickTenant && tenantId !== null ? { tenant_id: tenantId } : {}),
        });
        onOpenChange(false);
        onCreated?.(created);
      }
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not save the trigger.");
    }
  };

  const pathField = (field: MappingField, required = false, placeholder?: string) => (
    <Field
      key={field}
      label={FIELD_WORDS[field].label}
      htmlFor={`trigger-path-${field}`}
      required={required}
      hint={FIELD_WORDS[field].description || undefined}
    >
      <Input
        autoComplete="off"
        className="font-mono"
        list={PATHS_LIST}
        placeholder={placeholder ?? "a path, like contact.phone"}
        value={values.mapping[field]}
        onChange={(e) => setPath(field, e.target.value)}
      />
    </Field>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer" size="lg">
        <DiscardGuard changed={changed} />
        <DialogHeader>
          <DialogTitle>{trigger ? "Edit trigger" : "New trigger"}</DialogTitle>
          <DialogDescription>
            A URL another system calls when something happens there, and what the platform does about it.
          </DialogDescription>
        </DialogHeader>

        <DialogForm onSubmit={onSubmit} noValidate>
          <DialogBody className="space-y-5">
            {formError && <Callout tone="danger">{formError}</Callout>}
            {touched && problem && !formError && <Callout tone="danger">{problem}</Callout>}

            <div className="space-y-4">
              <Field label="Name" htmlFor="trigger-name" required hint="How this console lists it. Unique among your triggers.">
                <Input
                  autoComplete="off"
                  maxLength={200}
                  placeholder="Zapier: new leads from the intake form"
                  invalid={touched && !values.name.trim()}
                  value={values.name}
                  onChange={(e) => set("name", e.target.value)}
                />
              </Field>
              <Field label="Description" htmlFor="trigger-description" hint="Which zap or workflow calls it, for whoever reads this next.">
                <Textarea
                  rows={2}
                  className="min-h-14 font-sans text-sm"
                  maxLength={2000}
                  value={values.description}
                  onChange={(e) => set("description", e.target.value)}
                />
              </Field>

              {pickTenant && (
                <Field label="Tenant" htmlFor="trigger-tenant" required hint="Whose leads it acts on.">
                  <Select value={tenantId?.toString() ?? ""} onValueChange={(next) => setTenantId(Number(next))}>
                    <SelectTrigger id="trigger-tenant">
                      <SelectValue placeholder="Choose a tenant" />
                    </SelectTrigger>
                    <SelectContent>
                      {(tenants ?? []).map((t) => (
                        <SelectItem key={t.id} value={String(t.id)}>
                          {t.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              )}

              <Field label="What it does" htmlFor="trigger-action" required hint={actionTerm(action).description}>
                <Select value={action} onValueChange={(next) => set("action", next)}>
                  <SelectTrigger id="trigger-action">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TRIGGER_ACTIONS.map((a) => (
                      <SelectItem key={a} value={a} description={actionTerm(a).description}>
                        {actionTerm(a).label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              {!capture && (
                <Field
                  label="Campaign"
                  htmlFor="trigger-campaign"
                  required={enlist}
                  hint={
                    enlist
                      ? "The campaign whose dial list new leads go on."
                      : "Only this campaign's dial list, or every campaign the lead is on. Do-not-call is the lead's own, on every campaign."
                  }
                >
                  <Select
                    value={values.campaign_id || ALL}
                    onValueChange={(next) => set("campaign_id", next === ALL ? EVERY_CAMPAIGN : next)}
                  >
                    <SelectTrigger id="trigger-campaign" invalid={touched && enlist && !values.campaign_id}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL}>{enlist ? "Choose a campaign" : "Every campaign"}</SelectItem>
                      {tenantCampaigns.map((campaign) => (
                        <SelectItem key={campaign.id} value={campaign.id}>
                          {campaign.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              )}
            </div>

            {!capture && (
              <Section
                title="Where a request names the lead"
                description={
                  suggestions.length > 0
                    ? "Paths into what the sender sends. The fields of this trigger's newest request are suggested in each box."
                    : "Paths into what the sender sends: phone for a field named phone, contact.phone for one inside contact."
                }
              >
                {pathField("phone", enlist)}
                <div className="grid gap-4 sm:grid-cols-[1fr_12rem]">
                  {pathField("source_ref", enlist)}
                  <Field
                    label="Its source"
                    htmlFor="trigger-lead-source"
                    required={enlist || Boolean(values.mapping.source_ref.trim())}
                    hint="What those ids belong to: zapier, ghl."
                  >
                    <Input
                      autoComplete="off"
                      className="font-mono"
                      maxLength={100}
                      value={values.lead_source}
                      onChange={(e) => set("lead_source", e.target.value)}
                    />
                  </Field>
                </div>
                {!enlist && pathField("lead_id")}
              </Section>
            )}

            {action === "lead.hold" && (
              <Section title="The hold" description="Until the time a request names, or for a number of hours when it names none.">
                {pathField("until", false, "appointment.end_time")}
                <div className="grid gap-4 sm:grid-cols-3">
                  <Field label="Hours" htmlFor="trigger-hours" hint="24 when left empty.">
                    <Input inputMode="numeric" value={values.hours} onChange={(e) => set("hours", e.target.value)} />
                  </Field>
                  <Field label="Reason" htmlFor="trigger-reason" hint="appointment when left empty.">
                    <Input className="font-mono" value={values.reason} onChange={(e) => set("reason", e.target.value)} />
                  </Field>
                  <Field label="Times read in" htmlFor="trigger-timezone" hint="For a time with no zone.">
                    <Input
                      className="font-mono"
                      placeholder="America/Los_Angeles"
                      value={values.timezone}
                      onChange={(e) => set("timezone", e.target.value)}
                    />
                  </Field>
                </div>
              </Section>
            )}

            {action === "lead.release" && (
              <Section title="The release" description="Why the hold ended, as the lead's dial list row records it.">
                <Field label="Reason" htmlFor="trigger-reason" hint="released when left empty: no_show, cancelled.">
                  <Input className="font-mono" value={values.reason} onChange={(e) => set("reason", e.target.value)} />
                </Field>
              </Section>
            )}

            {enlist && (
              <Section
                title="A new lead"
                description="A number the platform already has is that lead, as it is — a mapping is read only when the lead is being made."
              >
                <div className="flex items-center justify-between gap-4 rounded-md border border-border px-3 py-2.5">
                  <div>
                    <p id="trigger-consent-label" className="text-xs font-medium text-text">
                      Needs consent evidence
                    </p>
                    <p className="text-xs text-text-muted">
                      On, a request carrying no moment of consent makes no lead. Off, the lead is made
                      without it.
                    </p>
                  </div>
                  <Switch
                    checked={requireConsent}
                    onCheckedChange={(on) => set("require_consent", on)}
                    aria-labelledby="trigger-consent-label"
                  />
                </div>
                {!requireConsent && (
                  <Callout tone="warning">
                    <span className="font-medium">These leads are never dialed.</span> A lead with no
                    consent evidence is created and shows on the campaign, but the dialer will not
                    call it. Use this for contacts whose consent lives in the system they came from,
                    to look up and call back by hand.
                  </Callout>
                )}
                {pathField("consent_at", requireConsent, "consented_at")}
                <Field
                  label="Consent wording"
                  htmlFor="trigger-consent-wording"
                  required={requireConsent}
                  hint="The words the leads agreed to, exactly as they saw them. Kept on each new lead."
                >
                  <Textarea
                    className="min-h-20 font-sans text-sm"
                    maxLength={4000}
                    invalid={touched && requireConsent && !values.consent_wording.trim()}
                    value={values.consent_wording}
                    onChange={(e) => set("consent_wording", e.target.value)}
                  />
                </Field>
                {pathField("tags", false, "tags")}
                <p className="-mt-1 text-xs text-text-muted">
                  A firm the platform knows is read off the tags and kept on the lead as its agency,
                  with that firm's state; <span className="font-mono">spanish</span> among them makes
                  the lead Spanish, and its absence English. Anything named below wins over the tags.
                </p>
                <p className="-mt-1 text-xs text-text-muted">
                  A tag naming one of your dispositions becomes the lead's. When several do, the
                  outcome wins over the activity — a lead tagged both signed and chase is signed —
                  and a lead whose tags say the case is over joins the campaign already off its dial
                  list.
                </p>
                <div className="grid gap-4 sm:grid-cols-2">
                  {pathField("first_name")}
                  {pathField("last_name")}
                  {pathField("language")}
                  {pathField("state")}
                  {pathField("timezone")}
                  <Field label="Times read in" htmlFor="trigger-timezone" hint="For a consent time with no zone.">
                    <Input
                      className="font-mono"
                      placeholder="America/Los_Angeles"
                      value={values.timezone}
                      onChange={(e) => set("timezone", e.target.value)}
                    />
                  </Field>
                </div>

                <div className="space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-medium text-text">More about the lead</p>
                      <p className="text-xs text-text-muted">
                        Anything else a request carries, kept on the lead as a named attribute: an email, a case type.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => set("attributes", [...values.attributes, { name: "", path: "" }])}
                    >
                      <Plus />
                      Add one
                    </Button>
                  </div>
                  {values.attributes.map((row, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Input
                        aria-label={`Attribute ${index + 1}, name`}
                        autoComplete="off"
                        className="w-40 font-mono"
                        placeholder="email"
                        value={row.name}
                        onChange={(e) =>
                          set("attributes", values.attributes.map((r, i) => (i === index ? { ...r, name: e.target.value } : r)))
                        }
                      />
                      <span className="text-xs text-text-muted">from</span>
                      <Input
                        aria-label={`Attribute ${index + 1}, path`}
                        autoComplete="off"
                        className="min-w-0 flex-1 font-mono"
                        list={PATHS_LIST}
                        placeholder="contact.email"
                        value={row.path}
                        onChange={(e) =>
                          set("attributes", values.attributes.map((r, i) => (i === index ? { ...r, path: e.target.value } : r)))
                        }
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Remove attribute ${index + 1}`}
                        onClick={() => set("attributes", values.attributes.filter((_, i) => i !== index))}
                      >
                        <X />
                      </Button>
                    </div>
                  ))}
                </div>

                <Field label="List name" htmlFor="trigger-list-name" hint="The campaign's Lists tab shows this trigger's leads under it. The trigger's name when left empty.">
                  <Input maxLength={200} value={values.list_name} onChange={(e) => set("list_name", e.target.value)} />
                </Field>
              </Section>
            )}

            <Section
              title="Sent twice"
              description="A request with the same Idempotency-Key header is kept once. Without one, these paths name the event, so a sender that fires twice with other bytes is still kept once. Name the event, not only the lead: a second booking for one lead is a new event."
            >
              {values.idempotency.map((path, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    aria-label={`Known by, path ${index + 1}`}
                    autoComplete="off"
                    className="min-w-0 flex-1 font-mono"
                    list={PATHS_LIST}
                    placeholder="appointment.id"
                    value={path}
                    onChange={(e) => set("idempotency", values.idempotency.map((p, i) => (i === index ? e.target.value : p)))}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Remove path ${index + 1}`}
                    onClick={() => set("idempotency", values.idempotency.filter((_, i) => i !== index))}
                  >
                    <X />
                  </Button>
                </div>
              ))}
              {values.idempotency.length < MAX_IDEMPOTENCY_PATHS && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => set("idempotency", [...values.idempotency, ""])}
                >
                  <Plus />
                  Add a path
                </Button>
              )}
            </Section>

            <Section title="The secret" description="The header a request carries the secret in.">
              <Field label="Header" htmlFor="trigger-secret-header" hint="Letters, digits and hyphens.">
                <Input
                  autoComplete="off"
                  className="font-mono"
                  maxLength={100}
                  value={values.secret_header}
                  onChange={(e) => set("secret_header", e.target.value)}
                />
              </Field>
            </Section>

            {trigger && (
              <div className="flex items-center justify-between gap-4 rounded-md border border-border px-3 py-2.5">
                <div>
                  <p id="trigger-enabled-label" className="text-xs font-medium text-text">
                    Takes requests
                  </p>
                  <p className="text-xs text-text-muted">
                    Off, its URL answers 404 and nothing is kept. What it already received stays readable.
                  </p>
                </div>
                <Switch
                  checked={values.enabled}
                  onCheckedChange={(checked) => set("enabled", checked)}
                  aria-labelledby="trigger-enabled-label"
                />
              </div>
            )}

            <datalist id={PATHS_LIST}>
              {suggestions.map((s) => (
                <option key={s.path} value={s.path}>
                  {s.example}
                </option>
              ))}
            </datalist>
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="default" loading={create.isPending || update.isPending}>
              {trigger ? "Save changes" : "Add trigger"}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}

function Section({
  title, description, children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3 border-t border-border pt-4">
      <div>
        <h3 className="text-sm font-medium text-text">{title}</h3>
        <p className="text-xs text-text-muted">{description}</p>
      </div>
      {children}
    </section>
  );
}
